/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Intercom integration for authenticated customer app.
 *
 * Handles:
 * - Widget loading & booting with user identification
 * - Custom user attributes (plan, bills, onboarding)
 * - HMAC identity verification
 * - Event tracking (bill uploaded, status changed, etc.)
 *
 * Required env vars:
 *   VITE_INTERCOM_APP_ID        – Intercom app ID (defaults to cp6lb23t)
 *   VITE_INTERCOM_IDENTITY_SECRET – (optional) HMAC secret for identity verification
 */

const INTERCOM_APP_ID = (import.meta.env.VITE_INTERCOM_APP_ID as string) || 'cp6lb23t';

/* -------------------------------------------------- */
/*  Intercom window type
/* -------------------------------------------------- */

interface IntercomWindow extends Window {
  Intercom?: (...args: unknown[]) => void;
  intercomSettings?: Record<string, unknown>;
}

interface IntercomStub {
  (...args: unknown[]): void;
  q: unknown[][];
  c: (args: unknown[]) => void;
}

/* -------------------------------------------------- */
/*  Script loader
/* -------------------------------------------------- */

let scriptLoaded = false;

function loadIntercomScript(): void {
  if (scriptLoaded) return;
  scriptLoaded = true;

  const w = window as IntercomWindow;
  if (typeof w.Intercom === 'function') return;

  const ic: IntercomStub = Object.assign(
    function (...args: unknown[]) {
      ic.q.push(args);
    },
    { q: [] as unknown[][], c: (args: unknown[]) => { ic.q.push(args); } }
  );
  w.Intercom = ic;

  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
  const x = document.getElementsByTagName('script')[0];
  x?.parentNode?.insertBefore(s, x);
}

/* -------------------------------------------------- */
/*  Boot / Update / Shutdown
/* -------------------------------------------------- */

export interface IntercomUserData {
  userId: string;
  email: string;
  name?: string;
  createdAt?: number; // Unix timestamp
  userHash?: string; // HMAC for identity verification
  // Custom attributes
  plan?: string;
  billsUsed?: number;
  billsLimit?: number;
  subscriptionStatus?: string;
  onboardingCompleted?: boolean;
  phone?: string;
}

/**
 * Boot Intercom with identified user data.
 * Call after authentication completes and user profile is loaded.
 */
/* Who Intercom is running as right now. A visitor boot (from the help
   link on login, signup or the code screen) has to be shut down before a
   signed-in user is booted, or the two get merged. */
let bootedAs: 'user' | 'visitor' | null = null;

export function bootIntercom(userData: IntercomUserData): void {
  loadIntercomScript();
  if (bootedAs === 'visitor') {
    (window as IntercomWindow).Intercom?.('shutdown');
  }
  bootedAs = 'user';

  const settings: Record<string, unknown> = {
    app_id: INTERCOM_APP_ID,
    api_base: 'https://api-iam.intercom.io',
    // The chat bubble shows on every page (16 Oct 2026). Before this it was
    // hidden and chat only opened from our own buttons.
    hide_default_launcher: false,
    ...launcherPosition(),
    user_id: userData.userId,
    email: userData.email,
  };

  if (userData.name) settings.name = userData.name;
  if (userData.createdAt) settings.created_at = userData.createdAt;
  if (userData.userHash) settings.user_hash = userData.userHash;
  if (userData.phone) settings.phone = userData.phone;

  // Custom attributes
  if (userData.plan) settings.plan = userData.plan;
  if (userData.billsUsed !== undefined) settings.bills_used = userData.billsUsed;
  if (userData.billsLimit !== undefined) settings.bills_limit = userData.billsLimit;
  if (userData.subscriptionStatus) settings.subscription_status = userData.subscriptionStatus;
  if (userData.onboardingCompleted !== undefined) settings.onboarding_completed = userData.onboardingCompleted;

  const w = window as IntercomWindow;
  w.intercomSettings = settings;

  if (typeof w.Intercom === 'function') {
    w.Intercom('boot', settings);
  }
}

/**
 * Update Intercom with new user attributes without rebooting.
 * Use when user data changes (e.g., bill submitted, plan changed).
 */
export function updateIntercom(attrs: Record<string, unknown>): void {
  const w = window as IntercomWindow;
  if (typeof w.Intercom === 'function') {
    w.Intercom('update', attrs);
  }
}

/**
 * Shut down Intercom on logout.
 */
export function shutdownIntercom(): void {
  bootedAs = null;
  const w = window as IntercomWindow;
  if (typeof w.Intercom === 'function') {
    w.Intercom('shutdown');
  }
}

/* -------------------------------------------------- */
/*  Event tracking
/* -------------------------------------------------- */

/**
 * Track a custom event in Intercom.
 */
function trackEvent(eventName: string, metadata?: Record<string, unknown>): void {
  const w = window as IntercomWindow;
  if (typeof w.Intercom === 'function') {
    w.Intercom('trackEvent', eventName, metadata);
  }
}

/** Track when a user uploads/submits a bill */
export function trackBillUploaded(metadata?: {
  provider?: string;
  billId?: string;
}): void {
  trackEvent('bill-uploaded', metadata);
}

/** Track when a bill status changes */
export function trackBillStatusChanged(metadata: {
  billId: string;
  oldStatus?: string;
  newStatus: string;
  provider?: string;
}): void {
  trackEvent('bill-status-changed', metadata);
}

/** Track when a payment/purchase is completed */
export function trackPaymentCompleted(metadata: {
  plan: string;
  amount: number;
  currency?: string;
}): void {
  trackEvent('payment-completed', {
    ...metadata,
    currency: metadata.currency || 'USD',
  });
}

/** Track when HIPAA consent is signed */
export function trackHipaaSigned(metadata?: {
  familyMemberId?: string;
}): void {
  trackEvent('hipaa-signed', metadata);
}

/** Track when onboarding is completed */
export function trackOnboardingCompleted(): void {
  trackEvent('onboarding-completed');
}

/** Track when a user views their bill results */
export function trackBillResultsViewed(metadata?: {
  billId?: string;
  savings?: number;
}): void {
  trackEvent('bill-results-viewed', metadata);
}

/**
 * Check whether the real Intercom SDK has loaded (not just the queue stub).
 * The queue stub we create in loadIntercomScript() has a `.q` array.
 * Once the real SDK loads, it replaces the stub with its own function
 * that does NOT have the `.q` array we created.
 */
function isRealIntercomLoaded(): boolean {
  const w = window as any;
  if (typeof w.Intercom !== 'function') return false;
  // Our stub has .q — the real SDK does not
  return !Array.isArray(w.Intercom.q);
}

/**
 * Opens chat for someone who isn't signed in (login, signup, code screen).
 * Boots Intercom as an anonymous visitor first if nothing is running yet.
 * The email they typed goes in the message itself rather than in the boot
 * settings, because identity verification won't accept an email without a
 * hash, and this way you still see who it is.
 */
export function openVisitorChat(prefill: string): void {
  bootVisitorIntercom();
  showIntercom(prefill);
}

/**
 * Starts Intercom for someone who isn't signed in, so the bubble shows on
 * login, signup and the code screen too. Does nothing if it's already running.
 */
export function bootVisitorIntercom(): void {
  loadIntercomScript();
  if (bootedAs) return;
  const settings = {
    app_id: INTERCOM_APP_ID,
    api_base: 'https://api-iam.intercom.io',
    hide_default_launcher: false,
    ...launcherPosition(),
  };
  const w = window as IntercomWindow;
  w.intercomSettings = settings;
  w.Intercom?.('boot', settings);
  bootedAs = 'visitor';
}

/**
 * How far up from the bottom the bubble sits. On a phone the app has a
 * 72px nav bar at the bottom, and the draft case page also has the start
 * button above that, so the bubble moves up to stay clear of both.
 */
export function launcherPosition(path: string = window.location.pathname): { vertical_padding: number } {
  const phone = window.matchMedia('(max-width: 900px)').matches;
  if (!phone) return { vertical_padding: 20 };
  // Case pages have the start button or the message box above the nav bar.
  if (path.startsWith('/case/')) return { vertical_padding: 190 };
  return { vertical_padding: 90 };
}

/** What the message box starts with when a free account asks before paying. */
export const ASK_BEFORE_START_PREFILL = 'Hi, I have a question about my bill before I start: ';

/**
 * Open the Intercom messenger panel.
 * Used by custom launcher buttons (the default launcher is hidden).
 *
 * Strategy:
 * 1. If the real SDK is loaded, call show() immediately
 * 2. Otherwise, ensure script is loading and poll until the real SDK
 *    replaces our stub (up to ~8 seconds)
 * 3. Fall back to mailto: help@mediloop.ai if Intercom never loads
 */
export function showIntercom(prefill?: string): void {
  const w = window as any;
  // With a prefill it opens a new message with those words already typed,
  // so the person only has to finish the sentence.
  const open = () => {
    if (prefill) w.Intercom('showNewMessage', prefill);
    else w.Intercom('show');
    /* If Intercom is loaded but won't open on this domain (it's switched off
       in Intercom's settings for my.mediloop.ai as of 16 Oct 2026), the
       click would do nothing at all. Fall back to email so they still
       reach someone. */
    setTimeout(() => {
      if (!document.querySelector('iframe[name="intercom-messenger-frame"]')) {
        const body = prefill ? `&body=${encodeURIComponent(prefill)}` : '';
        window.location.href = `mailto:help@mediloop.ai?subject=${encodeURIComponent('Question about my bill')}${body}`;
      }
    }, 4000);
  };

  // Ensure the script is loading
  loadIntercomScript();

  // If real SDK is ready, show immediately
  if (isRealIntercomLoaded()) {
    open();
    return;
  }

  // Real SDK hasn't replaced our stub yet — poll until it does
  let attempts = 0;
  const maxAttempts = 16; // 16 × 500ms = 8 seconds
  const interval = setInterval(() => {
    attempts++;
    if (isRealIntercomLoaded()) {
      clearInterval(interval);
      // Small extra delay to let Intercom finish internal init
      setTimeout(open, 200);
    } else if (attempts >= maxAttempts) {
      clearInterval(interval);
      // Intercom failed to load — open email fallback
      window.location.href = 'mailto:help@mediloop.ai?subject=Support%20Request';
    }
  }, 500);
}
